import type { Dispatch, SetStateAction } from "react";
import { useState, useMemo } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { IconButton, NoBorderButtonWrap } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { GenericComboboxFilter } from "app/pages/digital-marketing/common/filters/GenericComboboxFilter";
import { allTrackers } from "app/services/track/track";
import type { IChipItem } from "pro-features/components/MultiChipDown/src/MultiChipDownTypes";
import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { initialTableSort } from "../../../advertisers/campaigns/DisplayAdvertisingCampaigns";
import { useTableRows as useCampaignsTableRows } from "../../../advertisers/campaigns/useTableRows";
import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import { hasAdIntelligenceOverviewAccess, openAdIntelligenceHookModal } from "../../utils/utils";
import { useDebouncedSetSearchTerm } from "../hooks/useDebouncedSetSearchTerm";
import { useFilteredEntities } from "../hooks/useFilteredEntities";

import { AdIntelligenceAdsExtendedFilters } from "./AdIntelligenceAdsExtendedFilters";

const NUMBER_OF_RESULTS_TO_SHOW = 100;
const DATA_FETCH_PAGE = 1;
const DEBOUNCE_DELAY = 600;

const StyledNoBorderButtonWrap = styled(NoBorderButtonWrap)<{ $hasAdIntelBeta: boolean }>`
    cursor: ${({ $hasAdIntelBeta }) => ($hasAdIntelBeta ? "not-allowed" : "pointer")};
`;

const DisabledChipdownContent = styled(CenteredFlexRow)`
    gap: 4px;
    ${SWReactIcons} {
        margin-left: 0;
    }
`;

interface IAdIntelligenceAdsFilters {
    selectedCampaigns?: string[];
    selectedFormat?: string;
    onCampaignsFilterChange?: (campaigns: string[]) => void;
    onFormatFilterChange?: (format: string) => void;
    onEntityFilterChange?: (entities: string[]) => void;
    selectedEntity?: string[];
    controller?: AdIntelligenceController;
    isCompactView?: boolean;
    adsPageGoToState?: string;
    isFromNewScraper?: boolean;
    isPreview?: boolean;
}

export const AdIntelligenceAdsFilters = ({
    selectedCampaigns = [],
    selectedFormat,
    onCampaignsFilterChange,
    onFormatFilterChange,
    selectedEntity = [],
    onEntityFilterChange,
    controller = "advertiser",
    isCompactView = false,
    adsPageGoToState,
    isFromNewScraper,
    isPreview = false,
}: IAdIntelligenceAdsFilters) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { adsSelectedSite, key = "" } = params;
    const [campaignSearchTerm, setCampaignSearchTerm] = useState("");
    const [entitySearchTerm, setEntitySearchTerm] = useState("");
    const debouncedSetSearchTerm = useDebouncedSetSearchTerm(DEBOUNCE_DELAY);
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);

    const showCampaignsFilter = onCampaignsFilterChange !== undefined;

    // Use adsSelectedSite when in compare mode, otherwise use the main key
    const selectedSite = adsSelectedSite ?? key.split(",")[0];

    const { data: campaignsData, isLoading: isCampaignsLoading } = useCampaignsTableRows(
        selectedSite,
        DATA_FETCH_PAGE,
        NUMBER_OF_RESULTS_TO_SHOW,
        campaignSearchTerm,
        initialTableSort,
    );

    const { data: entityData, isLoading: isEntityLoading } = useFilteredEntities(controller, entitySearchTerm, true);

    const campaignsItems = useMemo(() => {
        return (
            campaignsData?.items?.map((item) => ({
                id: item.campaignId,
                text: item.campaignName,
            })) ?? []
        );
    }, [campaignsData?.items]);

    const entityItems = useMemo(() => {
        return (
            entityData?.map((item) => ({
                id: item.domain,
                text: item.domain,
            })) ?? []
        );
    }, [entityData]);

    const filterConfigs = useMemo(() => {
        const configs = [];

        if (showCampaignsFilter) {
            configs.push({
                show: true,
                items: campaignsItems,
                isLoading: isCampaignsLoading,
                selectedItems: selectedCampaigns,
                searchTerm: campaignSearchTerm,
                setSearchTerm: setCampaignSearchTerm,
                onFilterChange: onCampaignsFilterChange,
                entity: "Campaign" as const,
                paramKey: "selectedCampaigns" as const,
                width: "520px",
                tooltipKey: "ad.intelligence.ads.widget.filters.campaigns",
                placeholderKey: "traffic.sources.display.ads.campaigns.chipdown.placeholder",
                buttonTextKey: "traffic.sources.display.ads.campaigns.chipdown.text",
                useServerFiltering: true,
            });
        }

        // Entity filter config (Publishers or Advertisers) - always shown
        const entityName = controller === "advertiser" ? "Publisher" : "Advertiser";
        configs.push({
            show: true,
            items: entityItems,
            isLoading: isEntityLoading,
            selectedItems: selectedEntity,
            searchTerm: entitySearchTerm,
            setSearchTerm: setEntitySearchTerm,
            onFilterChange: onEntityFilterChange,
            entity: entityName,
            paramKey: `selected${entityName}s`,
            width: "320px",
            tooltipKey: `ad.intelligence.ads.widget.filters.${controller}s`,
            placeholderKey: `display.ads.${controller}s.entity.filter.placeholder`,
            buttonTextKey: `traffic.sources.display.ads.${controller}s.chipdown.text`,
            useServerFiltering: true,
            selector: "text" as const,
        });

        return configs;
    }, [
        showCampaignsFilter,
        campaignsItems,
        isCampaignsLoading,
        selectedCampaigns,
        campaignSearchTerm,
        entityItems,
        isEntityLoading,
        selectedEntity,
        entitySearchTerm,
        controller,
        onCampaignsFilterChange,
        onEntityFilterChange,
    ]);

    const handleFilterChange = (
        items: IChipItem[],
        entity: "Campaign" | "Publisher" | "Advertiser",
        onFilterChange: (items: string[]) => void,
        setSearchTerm: Dispatch<SetStateAction<string>>,
        paramKey: string,
    ) => {
        if (items.length) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter ${entity}/${items.length} selected`);
        }
        void proNavigator.updateParams(
            {
                ...params,
                [paramKey]: items.map((item) => item.id),
            },
            {
                reload: false,
            },
        );
        onFilterChange(items.map((item) => item.id));
        setSearchTerm("");
    };

    const onSearch = (term: string, setSearchTerm: Dispatch<SetStateAction<string>>) => {
        debouncedSetSearchTerm(term, setSearchTerm);
    };

    const handleMoreFiltersClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "More Filters");
        void proNavigator.go(adsPageGoToState, {
            ...params,
            selectedCampaigns,
            ...(controller === "advertiser"
                ? { selectedPublishers: selectedEntity }
                : { selectedAdvertisers: selectedEntity }),
        });
    };

    const handleUpgradeClick = () => {
        if (!hasAdIntelBeta) {
            openAdIntelligenceHookModal(controller);
        }
    };

    return (
        <>
            {isPreview ? (
                <>
                    {filterConfigs.map(
                        (config) =>
                            config.show && (
                                <a
                                    key={config.entity}
                                    onClick={handleUpgradeClick}
                                >
                                    <StyledNoBorderButtonWrap $hasAdIntelBeta={hasAdIntelBeta}>
                                        <DisabledChipdownContent>
                                            <SWReactIcons
                                                iconName="locked"
                                                size="xs"
                                            />
                                            <span>{translate(config.buttonTextKey)}</span>
                                        </DisabledChipdownContent>
                                    </StyledNoBorderButtonWrap>
                                </a>
                            ),
                    )}
                </>
            ) : (
                <>
                    {filterConfigs.map(
                        (config) =>
                            config.show && (
                                <GenericComboboxFilter
                                    key={config.entity}
                                    isLoading={config.isLoading}
                                    items={config.items}
                                    selectedItems={config.selectedItems}
                                    onSearch={(term: string) => onSearch(term, config.setSearchTerm)}
                                    onItemsFilterChange={(items: IChipItem[]) =>
                                        handleFilterChange(
                                            items,
                                            config.entity,
                                            config.onFilterChange,
                                            config.setSearchTerm,
                                            config.paramKey,
                                        )
                                    }
                                    width={config.width}
                                    tooltipContent={translate(config.tooltipKey)}
                                    placeholder={translate(config.placeholderKey)}
                                    buttonText={translate(config.buttonTextKey)}
                                    shouldUseServerFiltering={config.useServerFiltering}
                                    {...(config.selector && { selector: config.selector })}
                                />
                            ),
                    )}
                </>
            )}
            {isCompactView ? (
                hasAdIntelBeta && adsPageGoToState ? (
                    <IconButton
                        type="flatDark"
                        onClick={handleMoreFiltersClick}
                        iconName="chev-right"
                        placement={"right"}
                        height={32}
                    >
                        {translate("ad.intelligence.ads.widget.filters.more")}
                    </IconButton>
                ) : isPreview ? (
                    <a onClick={handleUpgradeClick}>
                        <StyledNoBorderButtonWrap $hasAdIntelBeta={hasAdIntelBeta}>
                            <DisabledChipdownContent>
                                <SWReactIcons
                                    iconName="locked"
                                    size="xs"
                                />
                                <span>{translate("ad.intelligence.ads.widget.filters.format")}</span>
                            </DisabledChipdownContent>
                        </StyledNoBorderButtonWrap>
                    </a>
                ) : (
                    <AdIntelligenceAdsExtendedFilters
                        selectedFormat={selectedFormat}
                        onFormatFilterChange={onFormatFilterChange}
                        isFromNewScraper={isFromNewScraper}
                    />
                )
            ) : isPreview ? (
                <a onClick={handleUpgradeClick}>
                    <StyledNoBorderButtonWrap $hasAdIntelBeta={hasAdIntelBeta}>
                        <DisabledChipdownContent>
                            <SWReactIcons
                                iconName="locked"
                                size="xs"
                            />
                            <span>{translate("ad.intelligence.ads.widget.filters.format")}</span>
                        </DisabledChipdownContent>
                    </StyledNoBorderButtonWrap>
                </a>
            ) : (
                <AdIntelligenceAdsExtendedFilters
                    selectedFormat={selectedFormat}
                    onFormatFilterChange={onFormatFilterChange}
                    isFromNewScraper={isFromNewScraper}
                />
            )}
        </>
    );
};
